import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { productsAPI, categoriesAPI, favoritesAPI, locationsAPI } from '../api';
import { useAuth } from '../hooks/useAuth';
import SEO from '../components/SEO';
import AdCard from '../components/AdCard';
import AdBanner from '../components/AdBanner';

const ProductsPage = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [favIds, setFavIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({});
  const [showFilters, setShowFilters] = useState(false);
  const [provinces, setProvinces] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [sectors, setSectors] = useState([]);
  const [filters, setFilters] = useState({
    category_id: searchParams.get('category_id') || '',
    province_id: searchParams.get('province_id') || '',
    district_id: searchParams.get('district_id') || '',
    sector_id: searchParams.get('sector_id') || '',
    min_price: '', max_price: '',
    search: searchParams.get('search') || '',
    page: 1,
  });
  const [searchInput, setSearchInput] = useState(searchParams.get('search') || '');
  const searchTimeout = useRef(null);

  const debouncedSearch = useCallback((value) => {
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => {
      setFilters(prev => ({ ...prev, search: value, page: 1 }));
    }, 400);
  }, []);

  // Sync filters when URL search params change
  useEffect(() => {
    const newSearch = searchParams.get('search') || '';
    setSearchInput(newSearch);
    setFilters(prev => ({
      ...prev,
      category_id: searchParams.get('category_id') || '',
      province_id: searchParams.get('province_id') || '',
      district_id: searchParams.get('district_id') || '',
      sector_id: searchParams.get('sector_id') || '',
      search: newSearch,
      page: 1,
    }));
  }, [searchParams]);

  useEffect(() => {
    categoriesAPI.getAll({ type: 'product' }).then(res => setCategories(res.data.tree || [])).catch(console.error);
    locationsAPI.getAll().then(res => {
      setProvinces(res.data.provinces || []);
      setDistricts(res.data.districts || []);
      setSectors(res.data.sectors || []);
    }).catch(console.error);
    if (user) favoritesAPI.getIds().then(res => setFavIds(res.data.favorites || [])).catch(() => {});
  }, [user]);

  useEffect(() => {
    setLoading(true);
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    productsAPI.getAll(params)
      .then(res => { setProducts(res.data.products || []); setPagination(res.data.pagination || {}); })
      .catch(console.error).finally(() => setLoading(false));
  }, [filters]);

  const isFav = (id) => favIds.some(f => f.item_type === 'product' && f.item_id === id);
  const handleToggleFav = (type, id, action) => {
    if (action === 'added') setFavIds(prev => [...prev, { item_type: type, item_id: id }]);
    else setFavIds(prev => prev.filter(f => !(f.item_type === type && f.item_id === id)));
  };
  const clearFilters = () => { setSearchInput(''); setFilters({ category_id: '', province_id: '', district_id: '', sector_id: '', min_price: '', max_price: '', search: '', page: 1 }); };
  const filteredDistricts = filters.province_id ? districts.filter(d => String(d.province_id) === String(filters.province_id)) : [];
  const filteredSectors = filters.district_id ? sectors.filter(s => String(s.district_id) === String(filters.district_id)) : [];

  const selectClass = "w-full border border-gray-200 rounded-xl px-3.5 py-2.5 text-sm focus:ring-2 focus:ring-primary-400/30 focus:border-primary-400 outline-none transition-all";

  return (
    <div className="bg-gray-50/50 min-h-screen">
      <SEO title="Products" description="Shop products from trusted sellers across Rwanda. Electronics, phones, clothing, home & garden, beauty, food, furniture, auto parts and more. New and used items at great prices in Kigali and nationwide." keywords="buy products Rwanda, online shop Kigali, marketplace Rwanda, electronics Rwanda, phones laptops Kigali, clothing fashion Rwanda, food beverages, home garden products, furniture Rwanda, beauty products Kigali, books stationery, baby kids products, sports outdoor, auto parts Rwanda, arts crafts, health products Kigali, secondhand used Rwanda, new products, affordable prices Rwanda, sell online Kigali, classified ads products, East Africa online shopping" />
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-primary-600 transition-colors">Home</Link>
          <svg className="w-4 h-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" /></svg>
          <span className="text-gray-900 font-medium">Products</span>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="flex gap-6">
          {/* Sidebar */}
          <div className={`${showFilters ? 'fixed inset-0 z-50 bg-white overflow-y-auto p-5 md:static md:z-auto md:bg-transparent md:p-0' : 'hidden'} md:block w-full md:w-64 flex-shrink-0`}>
            <div className="bg-white rounded-2xl border border-gray-100 p-5 md:sticky md:top-24 shadow-sm">
              <div className="flex items-center justify-between mb-5">
                <h3 className="font-bold text-gray-900">Filters</h3>
                <button onClick={clearFilters} className="text-xs text-primary-600 hover:text-primary-700 font-semibold">Clear all</button>
              </div>
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Search</label>
                  <input type="text" value={searchInput} onChange={(e) => { setSearchInput(e.target.value); debouncedSearch(e.target.value); }}
                    placeholder="Keywords..." className={selectClass} />
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Province</label>
                  <select value={filters.province_id} onChange={(e) => setFilters({...filters, province_id: e.target.value, district_id: '', sector_id: '', page: 1})} className={selectClass}>
                    <option value="">All provinces</option>
                    {provinces.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
                {filteredDistricts.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">District</label>
                    <select value={filters.district_id} onChange={(e) => setFilters({...filters, district_id: e.target.value, sector_id: '', page: 1})} className={selectClass}>
                      <option value="">All districts</option>
                      {filteredDistricts.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                    </select>
                  </div>
                )}
                {filteredSectors.length > 0 && (
                  <div>
                    <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Sector</label>
                    <select value={filters.sector_id} onChange={(e) => setFilters({...filters, sector_id: e.target.value, page: 1})} className={selectClass}>
                      <option value="">All sectors</option>
                      {filteredSectors.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                    </select>
                  </div>
                )}
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Category</label>
                  <select value={filters.category_id} onChange={(e) => setFilters({...filters, category_id: e.target.value, page: 1})} className={selectClass}>
                    <option value="">All categories</option>
                    {categories.map(cat => (
                      <React.Fragment key={cat.id}>
                        <option value={cat.id}>{cat.name}</option>
                        {cat.children?.map(sub => <option key={sub.id} value={sub.id}>&nbsp;&nbsp;{sub.name}</option>)}
                      </React.Fragment>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1.5 block">Price (RWF)</label>
                  <div className="flex gap-2">
                    <input type="number" placeholder="Min" value={filters.min_price}
                      onChange={(e) => setFilters({...filters, min_price: e.target.value, page: 1})} className={selectClass} />
                    <input type="number" placeholder="Max" value={filters.max_price}
                      onChange={(e) => setFilters({...filters, max_price: e.target.value, page: 1})} className={selectClass} />
                  </div>
                </div>
              </div>
              <button onClick={() => setShowFilters(false)} className="md:hidden w-full bg-primary-600 text-white py-2.5 rounded-xl font-semibold mt-4">Apply</button>
            </div>
          </div>

          {/* Main */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-5">
              <p className="text-sm text-gray-500"><span className="font-semibold text-gray-900">{pagination.total || products.length}</span> products found</p>
              <button onClick={() => setShowFilters(true)} className="md:hidden text-sm text-primary-600 font-semibold border border-primary-200 bg-primary-50 px-4 py-2 rounded-xl hover:bg-primary-100 transition-colors">
                <span className="flex items-center gap-1.5">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" /></svg>
                  Filters
                </span>
              </button>
            </div>
            <AdBanner placement="products-top" className="mb-4" />

            {loading ? (
                          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {[...Array(8)].map((_, i) => (
                  <div key={i} className="bg-white rounded-2xl border border-gray-100 animate-pulse overflow-hidden">
                    <div className="aspect-[4/3] bg-gray-100" /><div className="p-4 space-y-2"><div className="h-4 bg-gray-100 rounded-full w-2/3" /><div className="h-3 bg-gray-100 rounded-full" /></div>
                  </div>
                ))}
              </div>
            ) : products.length === 0 ? (
              <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                <div className="text-5xl mb-4">📦</div>
                <h3 className="text-lg font-bold text-gray-900 mb-1">No products found</h3>
                <p className="text-gray-500 text-sm mb-5">Try adjusting your filters</p>
                <button onClick={clearFilters} className="text-primary-600 font-semibold text-sm hover:text-primary-700">Clear all filters</button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {products.map(p => <AdCard key={p.id} item={p} type="product" isFav={isFav(p.id)} onToggleFav={handleToggleFav} />)}
                </div>
                {pagination.pages > 1 && (
                  <div className="flex justify-center mt-8 gap-1.5">
                    {Array.from({ length: Math.min(pagination.pages, 10) }, (_, i) => (
                      <button key={i+1} onClick={() => setFilters({...filters, page: i+1})}
                        className={`w-10 h-10 rounded-xl text-sm font-medium transition-all ${filters.page === i+1 ? 'bg-primary-600 text-white shadow-md shadow-primary-500/20' : 'bg-white text-gray-700 border border-gray-200 hover:bg-gray-50'}`}>{i+1}</button>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsPage;
